//@ts-ignore
import PageNotFound from '@/pages/404';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useSDMCartContext, useSDMCustomerContext } from '@ldp/sdm-contexts';

import { getStoreDetails } from '@/api/store-locator';
import NonSduiComponent from '@/components/non-sdui/non-sdui.component';
import StoreDetails from '@/components/store-details';
import { TLayout, TStoreDetailsResults } from '@/components/store-details/store-details.interface';
import { getLocationName, getStoreName } from '@/components/store-details/utils';
import { HOST, PAGE_ID } from '@/data/constants';
import { capitalizeEveryFirstLetter } from '@/utils';
import { useTrackPageView } from '@/hooks';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { isPharmaprixBanner } from 'Utils/utils';
import safeStringify from 'fast-safe-stringify';

export const getServerSideProps = async ({
  req,
  params,
  query,
}: {
  req: any;
  params: { storeId: string };
  query: any;
}) => {
  try {
    const { storeId } = params;
    const { lang } = query;
    const { response, b3ID } = await getStoreDetails(storeId, lang, req);
    return { props: { ...response, b3ID } };
  } catch (error: any) {
    Sentry.captureMessage('Failed to load store details data. ' + error.response);
    console.error('Failed to load store details data: ', safeStringify(error.response));
    return { props: { error: true } };
  }
};

const StoreDetailsPage = ({ layout, error, b3ID }: { layout: TLayout; error?: boolean; b3ID?: string }) => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const storeId = (router.query.storeId ?? '') as string;
  const tab = router.query.tab as string;
  const [isPhxPharmacyTab, setIsPhxPharmacyTab] = useState(false);
  const { cartCount, isInitialCartCountUpdated, setPageId } = useSDMCartContext();
  const { pcOptimumAccount } = useSDMCustomerContext();

  setPageId(PAGE_ID.STORE_DETAILS);
  useTrackPageView(cartCount, PAGE_ID.STORE_DETAILS, pcOptimumAccount, isInitialCartCountUpdated);

  console.log('bff data ====>', b3ID);

  if (error) {
    return <PageNotFound />;
  }

  const storeDetails = layout?.sections?.mainContentCollection?.components?.find(
    ({ componentId }) => componentId === 'storeDetailsComponent'
  )?.data as TStoreDetailsResults;
  const { address1, city, name } = storeDetails;

  const isPharmaprix = isPharmaprixBanner();
  const currentUrlBase = isPharmaprix ? HOST.PHX_HOME : HOST.SDM_HOME;

  const constructStoreDetailsMetaData = () => {
    const tabMap: { [key: string]: string } = {
      storeDetails: 'storeDetails',
      pharmacy: 'pharmacy',
      'pharmacist-walk-in-clinic': 'pharmacyClinic',
    };
    const storeInfoTab = tabMap[tab] || tabMap.storeDetails;

    const storeName = getStoreName(capitalizeEveryFirstLetter(name));
    const locationName = getLocationName(capitalizeEveryFirstLetter(name));
    const cityName = capitalizeEveryFirstLetter(city);
    const address = capitalizeEveryFirstLetter(address1);

    const seoTitle = formatMessage(
      { id: `${storeInfoTab}SeoTitle` },
      { name: storeName, location: locationName, city: cityName }
    );
    const seoDescription = formatMessage(
      { id: `${storeInfoTab}SeoDescription` },
      { name: storeName, address, location: locationName }
    );
    return {
      title: seoTitle,
      description: seoDescription,
      addMetaNoRobots: false,
      jsonLd: null,
    };
  };
  const seoMetaData = constructStoreDetailsMetaData();
  const seoWrapperData = {
    seoMetaData,
    currentUrlBase,
    jsonLdScript: '',
  };

  return (
    <NonSduiComponent {...seoWrapperData} isPhxPharmacyTab={isPhxPharmacyTab}>
      <StoreDetails
        layout={layout}
        storeId={storeId}
        isPhxPharmacyTab={isPhxPharmacyTab}
        setIsPhxPharmacyTab={setIsPhxPharmacyTab}
      />
    </NonSduiComponent>
  );
};

export default StoreDetailsPage;
